/**
 * API Client — Axios instances with Keycloak token injection.
 *
 * With the removal of the custom backend API, most auth operations go
 * through keycloak-js directly. This client is used for:
 * - Keycloak Admin REST API (via proxy if needed)
 * - Firebase Cloud Functions (called directly via firebase SDK)
 * - Any future CollabKare microservices
 *
 * For Keycloak direct REST API calls (e.g., user management),
 * we use the Keycloak base URL + realm path.
 */

import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { getToken } from "@/lib/keycloak";
import { useAuthStore } from "@/stores/auth-store";

// Keycloak server URL for direct REST API calls
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || "https://idp.collabkare.id";
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || "CollabKare";
const KEYCLOAK_BASE = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`;

// ─── Authenticated Axios Instance ──────────────────────────────────────────
// Uses Keycloak access token for all requests

export const apiClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: attach Keycloak token (refreshes automatically)
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Token refresh failed — Keycloak will handle redirect
      return Promise.reject(new Error("Authentication required"));
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid, trigger Keycloak login
      useAuthStore.getState().login();
    }
    return Promise.reject(error);
  }
);

// ─── Public Axios Instance (no auth) ───────────────────────────────────────

export const publicClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// ─── Keycloak REST API Endpoints ───────────────────────────────────────────
// Direct calls to Keycloak's REST API (replacing the old backend proxy)

export const KEYCLOAK_ENDPOINTS = {
  // Token endpoint (for service accounts, not for SPA — SPA uses keycloak-js)
  token: `${KEYCLOAK_BASE}/protocol/openid-connect/token`,

  // User info endpoint
  userInfo: `${KEYCLOAK_BASE}/protocol/openid-connect/userinfo`,

  // Account management (self-service)
  account: `${KEYCLOAK_BASE}/account`,

  // Password reset — Keycloak handles this via the login page's "Forgot Password" link
  // No custom endpoint needed; Keycloak sends reset emails directly.

  // Registration — Keycloak handles this via the login page's "Register" link
  // Custom registration can use Keycloak Admin REST API via Firebase Cloud Function.
} as const;

// ─── Convenience Methods ───────────────────────────────────────────────────

export async function apiGet<T = unknown>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse<T> = await apiClient.get(url, config);
  return response.data;
}

export async function apiPost<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse<T> = await apiClient.post(url, data, config);
  return response.data;
}

export async function apiPut<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse<T> = await apiClient.put(url, data, config);
  return response.data;
}

export async function publicPost<T = unknown>(
  url: string,
  data?: unknown
): Promise<T> {
  const response: AxiosResponse<T> = await publicClient.post(url, data);
  return response.data;
}

export async function publicGet<T = unknown>(url: string): Promise<T> {
  const response: AxiosResponse<T> = await publicClient.get(url);
  return response.data;
}
